import { audiences } from "@/mocks/genres";
import { resolveDisplayAttributes, type DisplayAttribute } from "@/lib/attributes";
import {
  findAttributeDefinition,
  findGenre,
  getAllAttributes,
  getAllFloors,
  getAllGenres,
  getCategorySnapshot,
  getConditionSet,
  getContentTypeRecord,
  getContentTypeRecords,
  getFloorById as findFloorById,
  getGenreDisplayName,
  getGenreGroupsFor,
  getGenresFor,
  isGenreVisible,
} from "@/lib/categorySnapshot";
import { filterGenres, resolveGenreIdsIn } from "@/lib/genreMatch";
import { normalizeText } from "@/lib/normalize";
import type { Work } from "@/types/work";
import type {
  AttributeDefinition,
  AudienceId,
  ContentTypeId,
  Floor,
  Genre,
} from "@/types/taxonomy";
import type { AudienceType } from "@/types/work";

/**
 * 分類（タクソノミー）の表示用API。
 *
 * 表示に必要なデータはカテゴリ・スナップショット（リポジトリから取得）を参照する。
 * 取得前はデモデータで補完されるため、画面が欠けることはない。
 * 外部APIに切り替えると、この関数群の戻り値がそのままAPI由来の値になる。
 */

export { audiences };

export function getContentTypes() {
  return getContentTypeRecords();
}

export function getGenreById(value: string): Genre | undefined {
  return findGenre(value);
}

/** URLパラメータなどの表記揺れを吸収してジャンルIDへ変換する */
export function resolveGenreIds(values: string[]): string[] {
  return resolveGenreIdsIn(getAllGenres(), values);
}

export function getGenreName(value: string): string {
  return getGenreDisplayName(value);
}

export function getGenreNames(values: string[]): string[] {
  return values.map((value) => getGenreName(value));
}

export function getFloorById(id: string): Floor | undefined {
  return findFloorById(id);
}

export function getContentTypeDefinition(id: ContentTypeId) {
  return getContentTypeRecord(id);
}

export function getContentTypeName(id: ContentTypeId): string {
  return getContentTypeRecord(id)?.name ?? "その他";
}

export function getAudienceName(id: AudienceId): string {
  return audiences.find((audience) => audience.id === id)?.name ?? "未分類";
}

export function getAttributeDefinition(id: string): AttributeDefinition | undefined {
  return findAttributeDefinition(id);
}

/** 対象モードで表示できるジャンルか（共通区分を含む） */
export function isGenreVisibleForAudience(genre: Genre, mode?: AudienceType): boolean {
  return isGenreVisible(genre, mode);
}

export function getGenresForAudience(mode?: AudienceType): Genre[] {
  return getGenresFor(mode);
}

/** フロア単位のグループ。ジャンル一覧・絞り込みUIはこれを動的に描画する */
export function getGenreGroups(mode?: AudienceType): { floor: Floor; genres: Genre[] }[] {
  return getGenreGroupsFor(mode);
}

export function searchGenres(query: string, mode?: AudienceType): Genre[] {
  return filterGenres(getAllGenres(mode), { mode, query });
}

export function getWorkGenres(work: Work): Genre[] {
  return work.genreIds
    .map((id) => findGenre(id))
    .filter((genre): genre is Genre => Boolean(genre));
}

export function getWorkGenreNames(work: Work, limit?: number): string[] {
  const names = getWorkGenres(work).map((genre) => genre.name);
  return typeof limit === "number" ? names.slice(0, limit) : names;
}

/**
 * 作品属性の表示用一覧。
 * その作品の商品形式で使う属性だけを、値が有効なものに限って返す。
 * （不明値や 0 を「0分」のように表示しない）
 */
export function getWorkAttributeList(work: Work): DisplayAttribute[] {
  const allowedIds = getConditionSet(work.contentType)?.attributeIds ?? [];
  return resolveDisplayAttributes(work.attributes, getAllAttributes(), {
    contentType: work.contentType,
    allowedIds,
  });
}

export function getWorkAttributes(
  work: Work,
): { id: string; name: string; value: string; display: string }[] {
  return getWorkAttributeList(work).map((attribute) => ({
    id: attribute.id,
    name: attribute.name,
    value: attribute.value,
    display: attribute.display,
  }));
}

/** 作品が保持する提供元のジャンルID・フロアID（将来のAPI連携用の情報） */
export function getWorkProviderRefs(work: Work) {
  return getWorkGenres(work).map((genre) => ({
    genreId: genre.providerGenreId,
    floorId: genre.providerFloorId,
    name: genre.name,
  }));
}

export function countGenresByAudience(): Record<AudienceId, number> {
  const counts: Record<AudienceId, number> = { male: 0, female: 0, common: 0, unclassified: 0 };
  getAllGenres().forEach((genre) => {
    genre.audienceIds.forEach((audience) => {
      counts[audience] += 1;
    });
  });
  return counts;
}

export function sortGenreIdsByDisplay(ids: string[]): string[] {
  return ids
    .slice()
    .sort((a, b) => {
      const genreA = findGenre(a);
      const genreB = findGenre(b);
      if (!genreA || !genreB) return 0;
      return genreA.sortOrder - genreB.sortOrder;
    });
}

/** フロア一覧（サービス区分で絞り込める） */
export function getFloorList(serviceId?: string | null): Floor[] {
  const floors = getAllFloors();
  if (!serviceId) return floors;
  const service = getCategorySnapshot().services.find((item) => item.id === serviceId);
  if (!service) return floors;
  return floors.filter((floor) => service.floorIds.includes(floor.id));
}

/** ジャンル名の検索用キー（保存済み条件の照合に使う） */
export function toGenreKey(value: string): string {
  return normalizeText(value);
}